import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  signal,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { MRunnerRowComponent } from "./m-runner-row/m-runner-row.component";
import { MatIconModule } from "@angular/material/icon";
import { AsyncPipe, NgClass, PercentPipe } from "@angular/common";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslateModule } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { MarketOpsService } from "../market-ops.service";
import { PnlCalculatorService } from "../../../../../../../../shared/src/lib/services/pnl-calculator.service";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatButton } from "@angular/material/button";
import { FeatureFlagService } from "../../../../../../../../shared/src/lib/services/feature-flag.service";
import { Runner } from "../../../../../../../../shared/src/lib/types/bet.type";
import { WhiteLabelConfigService } from "../../../../../../../../shared/src/lib/services/white-label-config.service";

export type MarketRunnerConfig = {
  coloredBacks: number[];
  coloredLays: number[];
};

@Component({
  selector: "app-market-runners",
  imports: [MRunnerRowComponent, MatIconModule, PercentPipe, AsyncPipe, NgClass, MatTooltipModule, TranslateModule, MatExpansionModule, MatButton],
  templateUrl: "./market-runners.component.html",
  styleUrl: "./market-runners.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketRunnersComponent implements OnDestroy, OnChanges, AfterViewInit {
  @Input() market: any;
  @Input() marketUpdate: any = null;
  @Input() config: MarketRunnerConfig = {
    coloredBacks: [1],
    coloredLays: [1],
  };
  @ViewChild("scrollContainer") scrollContainerRef!: ElementRef<HTMLDivElement>;
  marketOps = inject(MarketOpsService);
  wlConfigService = inject(WhiteLabelConfigService);
  updatedRunners = signal({});
  totalBackPercent = signal<number>(0);
  totalLayPercent = signal<number>(0);
  status = signal<string>("");
  isInPlay = signal<boolean>(false);
  dialog = inject(MatDialog);
  pnlCalculator = inject(PnlCalculatorService);
  featureFlagService = inject(FeatureFlagService);
  compactView = true;
  rowHeight = 70; // px
  firstVisibleRow = 0;
  lastVisibleRow = 0;
  afterViewInitTimeout: any = null;
  toggleCompactTimeout: any = null;

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.["marketUpdate"]) {
      const update = changes?.["marketUpdate"]?.currentValue;
      this.handleMarketUpdate(update);
    }
  }

  ngAfterViewInit() {
    if (this.afterViewInitTimeout) clearTimeout(this.afterViewInitTimeout);
    this.afterViewInitTimeout = setTimeout(() => {
      const container = this.scrollContainerRef.nativeElement;
      const firstRow = container.querySelector("tbody tr");
      if (firstRow) this.rowHeight = firstRow.getBoundingClientRect().height;
      this.scrollToCenter();
    }, 0);
  }

  openDebuggerWindow() {
    const marketId = this.market?.marketId;
    const url = `${window.location.origin}/market-debugger/${marketId}`;
    const win = window.open(url, "_blank", "width=1200,height=600");
  }

  handleMarketUpdate(update: any) {
    this.handleRunnerUpdates(update?.r);
    this.isInPlay.set(update?.ipl);
  }

  handleRunnerUpdates(runners: any[]) {
    let totalBackPrices = runners?.reduce((sum: number, runner: any) => {
      return sum + 1 / (runner?.ex?.abp?.[0] || 1);
    }, 0);
    let totalLayPrices = runners?.reduce((sum: number, runner: any) => {
      return sum + 1 / (runner?.ex?.alp?.[0] || 1);
    }, 0);

    // console.log(this.marketOps.transformRunners(runners));
    this.updatedRunners.set(this.marketOps.transformRunners(runners));
    this.totalBackPercent.set(totalBackPrices);
    this.totalLayPercent.set(totalLayPrices);
  }

  getRunnerUpdate(runner: any) {
    const selectionId = `${runner?.selectionId}${this.isAsianHandicap && runner?.handicap ? `_${runner?.handicap}` : ""}`;
    const runners: any = this.updatedRunners();
    return runners?.[selectionId];
  }

  get isAsianHandicap() {
    return this.market?.description?.bettingType?.includes("ASIAN_HANDICAP");
  }

  toggleCompact() {
    this.compactView = !this.compactView;

    if (this.toggleCompactTimeout) clearTimeout(this.toggleCompactTimeout);
    this.toggleCompactTimeout = setTimeout(() => {
      const firstRow = this.scrollContainerRef.nativeElement.querySelector("tbody tr");
      if (firstRow) {
        this.rowHeight = firstRow.getBoundingClientRect().height;
      }
      if (this.compactView) {
        this.scrollToCenter();
      } else {
        this.updateVisibleLines();
      }
    }, 0);
  }

  onScroll(container: HTMLElement) {
    const rows = Array.from(container.querySelectorAll("tbody tr"));
    const containerRect = container.getBoundingClientRect();

    let first = 0;
    let last = rows.length - 1;

    for (let i = 0; i < rows.length; i++) {
      const rowRect = rows[i].getBoundingClientRect();
      if (rowRect.bottom > containerRect.top) {
        first = i;
        break;
      }
    }

    for (let i = rows.length - 1; i >= 0; i--) {
      const rowRect = rows[i].getBoundingClientRect();
      if (rowRect.top < containerRect.bottom) {
        last = i;
        break;
      }
    }

    this.firstVisibleRow = first;
    this.lastVisibleRow = last + 1;
  }

  scrollTable(container: HTMLElement, amount: number) {
    if (container) container.scrollBy({ top: amount, behavior: "smooth" });
  }

  updateVisibleLines() {
    const container = this.scrollContainerRef.nativeElement;
    this.onScroll(container);
  }

  scrollToCenter() {
    const container = this.scrollContainerRef.nativeElement;
    const totalRunners = this.market?.runners?.length ?? 0;
    const middleRow = Math.floor(totalRunners / 2);
    const scrollPosition = middleRow * this.rowHeight;
    container.scrollTo({ top: scrollPosition, behavior: "instant" });
    this.updateVisibleLines();
  }

  trackBy(runner: Runner) {
    return runner?.selectionId + "_" + runner?.handicap;
  }

  ngOnDestroy(): void {
    if (this.afterViewInitTimeout) clearTimeout(this.afterViewInitTimeout);
    if (this.toggleCompactTimeout) clearTimeout(this.toggleCompactTimeout);
  }
}
